import { usePlatformConfig } from "../utils/platformConfig";
import { useLanguage } from "../context/LanguageContext";

/* The support number as every Help button dials it: the last ten digits of the company
   setting, and never the placeholder. Null when there is no usable number. */
export function useSupportDigits() {
  const cfg = usePlatformConfig();
  const digits = String(cfg?.supportPhoneNumber || "").replace(/\D/g, "").slice(-10);
  return digits.length === 10 && digits !== "9999999999" ? digits : null;
}

/**
 * "Need help with this lead?" - a partner calls Sramika support from a lead.
 *
 * Shown on every lead a business partner can open: the cab owner's Lead Details and
 * the hire driver's lead card. It carries a short reference built from the lead id,
 * read out on the call, so support finds the right lead without the partner having to
 * describe the trip.
 *
 * The number is the support number from company settings - the same one the header's
 * Support button dials - reduced to its last ten digits, so it dials correctly whether
 * the setting holds "8341265847" or "+918341265847". With no usable number it renders
 * nothing rather than a button that dials something wrong.
 *
 * The customer's enquiry sheet uses it too, with its own words (`label`) - the same
 * Ref on both sides, so support finds the lead whichever of them rings.
 */
export default function LeadHelpButton({ leadId, className = "", label = "Need help with this lead? Call Sramika" }) {
  const { t } = useLanguage();
  const digits = useSupportDigits();
  if (!digits) return null;

  const ref = String(leadId || "").replace(/^[A-Z]+#/i, "").replace(/[^A-Za-z0-9]/g, "").slice(-6).toUpperCase();

  return (
    <a
      href={`tel:+91${digits}`}
      onClick={(e) => e.stopPropagation()}
      className={`flex items-center justify-center gap-2 w-full rounded-xl border border-[#D7E8A8] bg-[#F6FAEC] px-3 py-2.5 text-[12px] font-bold text-[#4F7A00] active:scale-[0.99] transition-transform ${className}`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 16.92v2a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h2a2 2 0 0 1 2 1.72c.12.9.3 1.77.54 2.6a2 2 0 0 1-.45 2.11L7.1 9.9a16 16 0 0 0 6 6l1.47-1.05a2 2 0 0 1 2.11-.45c.83.24 1.7.42 2.6.54A2 2 0 0 1 22 16.92z" />
      </svg>
      <span>{label}</span>
      {ref && <span className="text-[10px] font-semibold text-[#6B8E23]">· {t("lh_ref")} {ref}</span>}
    </a>
  );
}
